import { IconButton, InputBase, List, ListItem, Typography, CircularProgress, Box, Chip } from "@mui/material";
import { Fragment, useState, useEffect } from "react";
import { Vocabulary } from "../Utils/Vocabulary";
import { Close, Search } from "@mui/icons-material";
import { getData } from "../Services/getData";
import { endpoints, localUrlEnum } from "../Utils/UrlEnum";
import GenericModal from "./GenericModal";
import { EWidths } from "../Utils/Enums";
import styles from "../Styles/globalSearch.module.css";
import { getFullName } from "../Utils/Utils";
import { useSearchParams } from "react-router-dom";

export default function GlobalSearchComponent() {
  const [searchField, setSearchField] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleChangeSearch = (event: any) => {
    setSearchField(event.target.value);
  };

  const performSearch = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return;
    }

    setIsSearching(true);
    setShowResults(true);

    // Add search term to URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set("globalSearch", searchTerm.trim());
    setSearchParams(newParams);

    try {
      const url = `${endpoints.ordersEndpoint}/paginated?page=0&perPage=20&search=${encodeURIComponent(searchTerm.trim())}&globalSearch=${encodeURIComponent(searchTerm.trim())}`;
      const response = await getData(url) as any;

      if (response && response.data && response.data.results) {
        setSearchResults(response.data.results);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchField("");
    setSearchResults([]);
    setShowResults(false);

    // Remove globalSearch param from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("globalSearch");
    setSearchParams(newParams);
  };

  const handleSearch = async () => {
    await performSearch(searchField);
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleResultClick = (orderId: string) => {
    const url = `${localUrlEnum.order}/${orderId}`;
    window.open(url, "_blank");
  };

  const handleCloseModal = () => {
    setShowResults(false);
  };

  // Load search term from URL on mount
  useEffect(() => {
    const globalSearchParam = searchParams.get("globalSearch");
    if (globalSearchParam) {
      setSearchField(globalSearchParam);
      performSearch(globalSearchParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <InputBase
        sx={{ ml: 3, flex: 2 }}
        placeholder={Vocabulary.search}
        value={searchField}
        onChange={handleChangeSearch}
        onKeyDown={handleKeyDown}
      />
      <IconButton
        onClick={searchField.length > 0 ? handleClearSearch : handleSearch}
        type="button"
        sx={{ p: "6px" }}
      >
        {searchField.length > 0 ? <Close /> : <Search />}
      </IconButton>

      <GenericModal
        open={showResults}
        onClose={handleCloseModal}
        title={Vocabulary.searchResults}
        maxWidth={EWidths.XL}
        width="40vw"
      >
        <Box className={styles.searchResultsContainer}>
          {isSearching ? (
            <Box className={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : searchResults.length > 0 ? (
            <List className={styles.resultsList}>
              {searchResults.map((order: any) => {
                const client = order.client_id;
                const instructor = order.instructor_user?.[0] || order.instructor_id?.user_id;
                const workPoint = client?.workPointId?.name

                return (
                  <ListItem
                    key={order._id}
                    className={styles.resultItem}
                    onClick={() => handleResultClick(order._id)}
                  >
                    <Box className={styles.resultContent}>
                      <Box className={styles.resultHeader}>
                        <Typography variant="h6" className={styles.clientName}>
                          {getFullName(client)}
                        </Typography>
                        <Typography variant="body1" className={styles.productName}>
                          {order.product?.name || Vocabulary.noData}
                        </Typography>
                      </Box>

                      <Box className={styles.resultDetails}>
                        <Box className={styles.detailRow}>
                          <Typography variant="body2" className={styles.detailLabel}>
                            CNP:
                          </Typography>
                          <Typography variant="body2" className={styles.detailValue}>
                            {client?.CNP || Vocabulary.noData}
                          </Typography>
                        </Box>

                        <Box className={styles.detailRow}>
                          <Typography variant="body2" className={styles.detailLabel}>
                            Status:
                          </Typography>
                          <Box className={styles.detailValue}>
                            <Chip
                              label={order.status?.name || Vocabulary.noData}
                              size="small"
                              sx={{
                                backgroundColor: order.status?.color || "#ccc",
                                color: order.status?.color?.toLowerCase() === "#ffffff" ||
                                       order.status?.color?.toLowerCase() === "#fff" ||
                                       order.status?.color?.toLowerCase() === "white"
                                  ? "#000"
                                  : "#fff",
                                fontWeight: 600,
                                height: "24px"
                              }}
                            />
                          </Box>
                        </Box>

                        <Box className={styles.detailRow}>
                          <Typography variant="body2" className={styles.detailLabel}>
                            {Vocabulary.instructor}:
                          </Typography>
                          <Typography variant="body2" className={styles.detailValue}>
                            {instructor ? getFullName(instructor) : Vocabulary.noData}
                          </Typography>
                        </Box>

                        <Box className={styles.detailRow}>
                          <Typography variant="body2" className={styles.detailLabel}>
                            {Vocabulary.workPoint}:
                          </Typography>
                          <Typography variant="body2" className={styles.detailValue}>
                            {workPoint}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Box className={styles.noResults}>
              <Typography variant="body1">{Vocabulary.noData}</Typography>
            </Box>
          )}
        </Box>
      </GenericModal>
    </Fragment>
  );
}
